import type { HexTileMap } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import {
  type Building,
  extraTilesAllowed,
  growthCandidates,
  maxLevel,
  type SimState,
} from '../sim';

/** Names (one, several) of the land a growing building spreads over. */
const LAND_NAMES: Record<string, [string, string]> = {
  field: ['field', 'fields'],
  forest: ['forest', 'forests'],
  default: ['grass', 'grass'],
};

/**
 * "room to grow: 2 fields nearby" for a growing building (farm, forestry, barracks),
 * placed or about to be: matching free land next to it in its city's
 * territory (own territory is always in sight, so this leaks nothing).
 * "no room to grow" when there's none; null when it doesn't grow, or has
 * already taken every tile its top level allows.
 */
export function roomToGrow(
  state: SimState,
  ctx: { terrain: HexTileMap; rules: RuleSet },
  b: Building
): string | null {
  const def = ctx.rules.buildings[b.type];
  if (!def?.growth) return null;
  const left = extraTilesAllowed(def, maxLevel(def)) - (b.footprint?.length ?? 0);
  if (left <= 0) return null;
  const count = growthCandidates(state, ctx, b).length;
  if (count === 0) return 'no room to grow';
  const land = (def.placement?.on ?? [])
    .map((t) => LAND_NAMES[t]?.[count === 1 ? 0 : 1] ?? t)
    .join('/');
  return `room to grow: ${count} ${land} nearby`;
}
